import { Component, Suspense, useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Canvas, useLoader } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import * as THREE from "three";

/**
 * `matte` flattens every material (the pillow look, same as the Shopify viewer); `pbr` keeps the
 * GLB's own materials (glass transmission, satin sheen, metals) and lights them with an
 * environment map.
 */
export type ViewerShading = "matte" | "pbr";
import {
  Box,
  Center,
  ColorInput,
  Group,
  Loader,
  Select,
  Slider,
  Stack,
  Switch,
  Text,
} from "@mantine/core";

// ── Error boundary ────────────────────────────────────────────────────────────

interface ErrorBoundaryState { error: Error | null }

class ViewerErrorBoundary extends Component<{ children: ReactNode; onError: (e: Error) => void }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error) {
    this.props.onError(error);
  }

  render() {
    if (this.state.error) return null;
    return this.props.children;
  }
}

// ── Scene components ──────────────────────────────────────────────────────────

function Lighting({ intensity, shading }: { intensity: number; shading: ViewerShading }) {
  // With an environment map the IBL carries the scene; the flat ambient fill the matte pillow
  // needs would blow glass and satin out to white.
  const ambient = shading === "pbr" ? intensity * 0.35 : intensity * 2.5;
  return (
    <>
      <ambientLight intensity={ambient} />
      {/* Shadow caster (no visual contribution) */}
      <spotLight
        position={[0, 7, 0.5]}
        intensity={0}
        angle={0.75}
        penumbra={0.35}
        castShadow
        shadow-bias={-0.0005}
        shadow-normalBias={0.04}
      />
      <spotLight position={[0, 3, -6]} intensity={intensity * 50} angle={0.45} penumbra={0.15} />
      <spotLight position={[5.2, 3, 3]} intensity={intensity * 50} angle={0.45} penumbra={0.15} />
      <spotLight position={[-5.2, 3, 3]} intensity={intensity * 50} angle={0.45} penumbra={0.15} />
    </>
  );
}

/**
 * Image-based lighting from three's built-in RoomEnvironment (no network HDR). Installed once
 * when the canvas is created: the viewer is keyed per model, so the whole canvas (and with it
 * the environment) goes away with the model.
 */
function installEnvironment(gl: THREE.WebGLRenderer, scene: THREE.Scene) {
  const pmrem = new THREE.PMREMGenerator(gl);
  const target = pmrem.fromScene(new RoomEnvironment(), 0.04);
  scene.environment = target.texture;
  scene.environmentIntensity = 0.75;
  pmrem.dispose();
}

function Floor({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.374, 0]} receiveShadow>
      <planeGeometry args={[15, 15]} />
      <shadowMaterial opacity={0.15} />
    </mesh>
  );
}

interface ModelProps {
  url: string;
  shading: ViewerShading;
  onLoaded: () => void;
}

function Model({ url, shading, onLoaded }: ModelProps) {
  if (!url) throw new Error("No model URL provided");
  const gltf = useLoader(GLTFLoader, url);
  const called = useRef(false);

  useEffect(() => {
    const scene = gltf.scene;

    // Center model at origin
    const box = new THREE.Box3().setFromObject(scene);
    const center = new THREE.Vector3();
    box.getCenter(center);
    scene.position.sub(center);

    // Shadows always; matte materials only for the matte look (same as the shopify viewer).
    scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      if (shading === "matte") {
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        mats.forEach((mat) => {
          const m = mat as THREE.MeshStandardMaterial;
          if (m.isMeshStandardMaterial) {
            m.roughness = 1;
            m.metalness = 0;
            m.envMapIntensity = 0;
          }
        });
      }
      mesh.castShadow = true;
      mesh.receiveShadow = true;
    });

    if (!called.current) {
      called.current = true;
      onLoaded();
    }
  }, [gltf, shading, onLoaded]);

  return <primitive object={gltf.scene} />;
}

// ── Public component ──────────────────────────────────────────────────────────

const BG_SWATCHES = ["#ffffff", "#f0f0f0", "#d0d0d0", "#1a1a2e", "#0d0d0d"];

const TONE_MAPPING_OPTIONS = [
  { value: "none", label: "No tone mapping" },
  { value: "linear", label: "Linear" },
  { value: "reinhard", label: "Reinhard" },
  { value: "cineon", label: "Cineon" },
  { value: "aces", label: "ACES Filmic" },
];

function getToneMapping(v: string): THREE.ToneMapping {
  switch (v) {
    case "linear": return THREE.LinearToneMapping;
    case "reinhard": return THREE.ReinhardToneMapping;
    case "cineon": return THREE.CineonToneMapping;
    case "aces": return THREE.ACESFilmicToneMapping;
    case "none": return THREE.NoToneMapping;
    default: throw new Error(`Unknown tone mapping: ${v}`);
  }
}

interface GlbViewerProps {
  url: string;
  /** Defaults to `matte` (pillows). Ornaments pass `pbr`. */
  shading?: ViewerShading;
}

export default function GlbViewer({ url, shading = "matte" }: GlbViewerProps) {
  const [bgColor, setBgColor] = useState("#018574");
  const [autoRotate, setAutoRotate] = useState(true);
  const [showFloor, setShowFloor] = useState(true);
  const [lightIntensity, setLightIntensity] = useState(0.75);
  const [toneMapping, setToneMapping] = useState(shading === "pbr" ? "aces" : "none");
  // Load state keyed by url — derived rather than reset via an effect, so a new
  // url cleanly shows loading again without a synchronous setState in an effect.
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [errorState, setErrorState] = useState<{ url: string; message: string } | null>(null);

  const handleLoaded = useCallback(() => setLoadedUrl(url), [url]);
  const handleError = useCallback(
    (e: Error) => setErrorState({ url, message: e.message || "Failed to load model" }),
    [url],
  );

  const loadError = errorState?.url === url ? errorState.message : null;
  const loading = loadedUrl !== url && !loadError;

  return (
    <Stack gap="sm">
      {/* Canvas */}
      <Box
        style={{
          position: "relative",
          height: 400,
          borderRadius: 8,
          overflow: "hidden",
          border: "1px solid var(--mantine-color-default-border)",
        }}
      >
        <Canvas
          gl={{
            antialias: true,
            outputColorSpace: THREE.SRGBColorSpace,
            toneMapping: getToneMapping(toneMapping),
            toneMappingExposure: 1,
          }}
          shadows={{ type: THREE.PCFShadowMap }}
          camera={{ position: [0, 0, 4.35], fov: 32, zoom: 1.05 }}
          style={{ height: "100%" }}
          onCreated={({ gl, scene }) => {
            if (shading === "pbr") installEnvironment(gl, scene);
          }}
        >
          <color attach="background" args={[bgColor]} />
          <Lighting intensity={lightIntensity} shading={shading} />
          <Floor visible={showFloor} />
          <ViewerErrorBoundary onError={handleError}>
            <Suspense fallback={null}>
              <Model url={url} shading={shading} onLoaded={handleLoaded} />
            </Suspense>
          </ViewerErrorBoundary>
          <OrbitControls
            autoRotate={autoRotate}
            autoRotateSpeed={1.5}
            enablePan={false}
            minDistance={2}
            maxDistance={10}
          />
        </Canvas>

        {loading && (
          <Center
            style={{
              position: "absolute",
              inset: 0,
              backdropFilter: "blur(4px)",
              background: bgColor + "cc",
            }}
          >
            <Stack align="center" gap="xs">
              <Loader size="sm" />
              <Text size="xs" c="dimmed">Loading model…</Text>
            </Stack>
          </Center>
        )}

        {loadError && (
          <Center
            style={{
              position: "absolute",
              inset: 0,
              background: "var(--mantine-color-default)",
            }}
          >
            <Stack align="center" gap="xs" px="md">
              <Text size="sm" c="red" ta="center">Failed to load model</Text>
              <Text size="xs" c="dimmed" ta="center">{loadError}</Text>
            </Stack>
          </Center>
        )}
      </Box>

      {/* Controls */}
      <Group gap="lg" wrap="wrap" align="flex-end">
        <ColorInput
          label="Background"
          value={bgColor}
          onChange={setBgColor}
          format="hex"
          size="xs"
          w={150}
          swatches={BG_SWATCHES}
        />

        <Select
          label="Tone mapping"
          value={toneMapping}
          onChange={(v) => {
            if (v === null) throw new Error("Tone mapping cannot be empty");
            setToneMapping(v);
          }}
          data={TONE_MAPPING_OPTIONS}
          allowDeselect={false}
          size="xs"
          w={160}
        />

        <Stack gap={4}>
          <Text size="xs" fw={500}>Light intensity</Text>
          <Slider
            value={lightIntensity}
            onChange={setLightIntensity}
            min={0}
            max={3}
            step={0.1}
            w={140}
            size="xs"
          />
        </Stack>

        <Switch
          label="Auto-rotate"
          checked={autoRotate}
          onChange={(e) => setAutoRotate(e.currentTarget.checked)}
          size="sm"
        />

        <Switch
          label="Floor shadow"
          checked={showFloor}
          onChange={(e) => setShowFloor(e.currentTarget.checked)}
          size="sm"
        />
      </Group>
    </Stack>
  );
}
